import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'
import type {
  CreateAssetRequest,
  AssetVersion
} from '../types'

export interface AssetOData {
  id: string
  name: string
  type: string
  size: number
  url: string
  thumbnailUrl?: string
  version: number
  tags: string[]
  folderId?: string
  createdAt: string
  updatedAt: string
}

export interface AssetsODataResponse {
  value: AssetOData[]
  '@odata.count'?: number
}

export const assetService = {
  /**
   * Gets assets with OData query support.
   * Supports filtering, sorting, paging via OData parameters.
   */
  async getAssets(params?: ODataQueryParams): Promise<AssetsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<AssetsODataResponse | AssetOData[]>(`/v1/assets${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getAsset(id: string): Promise<AssetOData> {
    try {
      const response = await api.get<AssetOData>(`/v1/assets/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async uploadAsset(data: CreateAssetRequest, onProgress?: (progress: number) => void): Promise<AssetOData> {
    try {
      const formData = new FormData()
      formData.append('file', data.file)

      if (data.name) formData.append('name', data.name)
      if (data.description) formData.append('description', data.description)
      if (data.folderId) formData.append('folderId', data.folderId)
      if (data.tags && data.tags.length > 0) {
        formData.append('tags', JSON.stringify(data.tags))
      }

      const response = await api.post<AssetOData>('/v1/assets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
      })

      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async uploadMultipleAssets(
    files: File[],
    options?: Partial<CreateAssetRequest>,
    onProgress?: (fileIndex: number, progress: number) => void
  ): Promise<AssetOData[]> {
    try {
      const promises = files.map((file, index) =>
        this.uploadAsset(
          { ...options, file },
          (progress) => onProgress?.(index, progress)
        )
      )

      return Promise.all(promises)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async updateAsset(id: string, data: {
    name?: string
    description?: string
    folderId?: string
  }): Promise<AssetOData> {
    try {
      const response = await api.put<AssetOData>(`/v1/assets/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async deleteAsset(id: string): Promise<void> {
    try {
      await api.delete(`/v1/assets/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async deleteMultipleAssets(ids: string[]): Promise<void> {
    try {
      await api.post('/v1/assets/bulk-delete', { ids })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async moveAssets(assetIds: string[], folderId: string | null): Promise<void> {
    try {
      await api.post('/v1/assets/bulk-move', { assetIds, folderId })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async downloadAsset(id: string): Promise<Blob> {
    try {
      const response = await api.get<Blob>(`/v1/assets/${id}/download`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async generateThumbnail(id: string): Promise<string> {
    try {
      const response = await api.post<{ thumbnailUrl: string }>(
        `/v1/assets/${id}/generate-thumbnail`
      )
      return response.data.thumbnailUrl
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async addAssetTags(id: string, tags: string[]): Promise<void> {
    try {
      await api.post(`/v1/assets/${id}/tags`, { tags })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async removeAssetTag(id: string, tag: string): Promise<void> {
    try {
      await api.delete(`/v1/assets/${id}/tags/${tag}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getAssetVersions(id: string): Promise<AssetVersion[]> {
    try {
      const response = await api.get<AssetVersion[]>(`/v1/assets/${id}/versions`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async restoreAssetVersion(assetId: string, versionId: string): Promise<AssetOData> {
    try {
      const response = await api.post<AssetOData>(
        `/v1/assets/${assetId}/versions/${versionId}/restore`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async shareAsset(id: string, options: {
    expiresIn?: number // hours
    password?: string
    allowDownload?: boolean
  }): Promise<{ shareUrl: string }> {
    try {
      const response = await api.post<{ shareUrl: string }>(
        `/v1/assets/${id}/share`,
        options
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async revokeShareLink(id: string): Promise<void> {
    try {
      await api.delete(`/v1/assets/${id}/share`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Utility function to get file type icon
  getFileTypeIcon(type: string): string {
    const iconMap: Record<string, string> = {
      'Image': '🖼️',
      'Video': '🎥',
      'Audio': '🎵',
      'Document': '📄',
      'Spreadsheet': '📊',
      'Presentation': '📽️',
      'Archive': '📦',
      'Other': '📎',
    }
    return iconMap[type] || '📎'
  },

  // Utility function to format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  },
}
